import Commons from './Commons';
import { SUGGEST_FILTERS } from './Localization';
import { isNZ } from '../constants/crux';
import SUGGESTION_TYPES from '../constants/suggestionTypes';
import CLApi from '../api/clapi';
import { routeCodes } from '../constants/routes';
import { cruxAppError } from '../actions/errorHandler';
import ErrorMsg from '../constants/errorMsg';
import { ErrorType } from '../constants/redirect';
import { cruxUnloader } from '../actions/crux';
import {
    AND_OPERATOR,
    SORT_PARAM,
    RESULTS_FORMAT,
    RESULTS_FORMAT_SORT,
    RADIUS_SEARCH_FORMAT,
    LISTING_DATA_PARAM,
    VOLUME_FOLIO_DATA_PARAM,
    TENURE_DATA_PARAM,
    OR_OPERATOR,
    IS_FOR_SALE,
    DEFAULT_RESULTS_FORMAT,
    IS_ACTIVE_PARAM,
    OFFSET_PARAM,
    LIMIT_PARAM,
    METADATA_PARAM,
    DISTINCT_FIELDS_PARAM,
    RADIUS_PARAM,
    TYPE_PARAM,
    VOLUME_PARAM,
    FOLIO_PARAM,
    EXCLUDE_PROPERTY,
    MAP_SEARCH,
    SHAPE,
    ADDRESS_BUILDING_NAME_PARAM,
    TERRITORIES_SEARCH_FORMAT,
    AND_NOT_OPERATOR,
    EXISTS_OPERATOR,
} from 'js/constants/batchSearch/batchSearch';
import RapidBatchSearch from 'js/helpers/search/batch/RapidBatchSearch';
import SearchBuilder from 'js/helpers/search/batch/SearchBuilder';
import OTM from 'js/constants/otm';
import RapidSearchConstant from 'js/constants/rapidSearch';
import ListPropertyPreview from '../constants/listPropertyPreview';

const buildAddressSearchParam = (suggestionType, searchString) => {
    const addressParams = searchString.split(',');
    const clonedAddressSearchObj = {};
    let idx = 0;
    if (suggestionType === SUGGESTION_TYPES.STREET) {
        idx = 1;
        clonedAddressSearchObj.addressStreet = addressParams[0] && addressParams[0].trim();
    }
    clonedAddressSearchObj.addressSuburb = addressParams[idx] && addressParams[idx].trim();
    clonedAddressSearchObj.addressTown = addressParams[1 + idx] && addressParams[1 + idx].trim();
    return clonedAddressSearchObj;
};

const buildSuggestionRapidSearchFilter = (suggestion) => {
    const { suggestion: searchString, suggestionType } = suggestion || {};
    if (isNZ) {
        if (suggestionType === SUGGESTION_TYPES.STREET) {
            if (searchString && !searchString.includes(',')) {
                return { [SUGGEST_FILTERS.streetOnly]: searchString };
            }
            return buildAddressSearchParam(suggestionType, searchString);
        } else if (suggestionType === SUGGESTION_TYPES.LOCALITY) {
            return buildAddressSearchParam(suggestionType, searchString);
        }
    }
    // default return
    return searchString && suggestionType ?
        { [SUGGEST_FILTERS[suggestionType]]: searchString } : {};
};

const buildMultiLocalitySuggestion = (search) => ({
    [SUGGEST_FILTERS[search?.sType]]: search?.suggestions,
});

const flatten = (filters, ownerCompanySearch, volumeFolioSearch) =>
    (filters &&
    (JSON.stringify(ownerCompanySearch) !== '{}'
        || JSON.stringify(volumeFolioSearch) !== '{}')
        ? filters.flat()
        : filters);

const buildResultsFormat = (params, builder = RapidBatchSearch.batchParamBuilder) => {
    return [
        { param: OFFSET_PARAM },
        { param: LIMIT_PARAM },
        { param: SORT_PARAM, formatter: RESULTS_FORMAT_SORT },
        { param: METADATA_PARAM },
        { param: DISTINCT_FIELDS_PARAM },
        { param: SHAPE },
    ].reduce((resultsFormat, field) => {
        if (field.param in params && !!params[field.param]) {
            return builder(
                field.formatter ?? RESULTS_FORMAT,
                { resultsFormat, param: field.param, paramValue: params[field.param] },
            );
        }
        return resultsFormat;
    }, DEFAULT_RESULTS_FORMAT);
}

// TODO: Find a way to break out specific filters by search type,
//  this method caters a lot of things already. We might need to cater multiple property id as
//  parameter in the future, it will be easier to add another filter if we break things out.
const buildRequest = (searchFilters) => {
    const {
        statusType,
        propertyIds,
        offset,
        limit,
        sort,
        metadata,
        distinctFields,
        radius,
        lat,
        lon,
        tenureData,
        volumeNumber,
        folioNumber,
        volumeFolioData,
        listingData,
        ownerFirstName,
        ownerLastName,
        companyName,
        isCurrentOwner,
        exactSearch,
        propertyId,
        addressBuildingName,
        mapSearch,
        geoPolygon,
        exists,
        ...params
    } = JSON.parse(JSON.stringify(searchFilters));
    let outerFilters = [];
    const filters = [];

    // suburbOnly param is not needed because we can determine if we are targeting
    // for suburb only using the addressSuburb & addressPostcodeState params
    delete params.suburbOnly;
    // salesLastSaleContractDate is handled in the RECENT_SALE block with the nested salesList OR
    if (statusType === OTM.RECENT_SALE) {
        delete params.salesLastSaleContractDate;
    }
    let ownerCompanySearch = {};
    let volumeFolioSearch = {};
    const builder = statusType === OTM.WITHDRAWN
        ? RapidBatchSearch.batchWithdrawnParamBuilder
        : RapidBatchSearch.batchParamBuilder;
    const isMapInSearchFilters = MAP_SEARCH in searchFilters;
    Object.entries(params).forEach(([param, paramValue]) => {
        const handleFilter = (value) => filters.push(builder(param, { param, paramValue: value }));

        if (typeof paramValue === 'string' && paramValue.includes('|')) {
            // Handle multiple values
            const multiParams = paramValue.split('|');
            filters.push({
                operation: OR_OPERATOR,
                filters: multiParams.map(multiParam => builder(param, { param, paramValue: multiParam })),
            });
        } else if (IS_FOR_SALE.includes(param) && paramValue === false) {
            handleFilter(paramValue);
        } else if (IS_ACTIVE_PARAM.includes(param)) {
            if (typeof paramValue === 'string' && paramValue.includes('false')) {
                handleFilter(paramValue);
            }
        } else if (paramValue && !(param === TYPE_PARAM && statusType === OTM.TENURE)) {
            if (!Array.isArray(paramValue) || paramValue.length) {
                handleFilter(paramValue);
            }
        }
    });

    if ('companyName' in searchFilters
        || 'ownerLastName' in searchFilters) {
        ownerCompanySearch = true;
        filters.push(builder(
            LISTING_DATA_PARAM,
            {
                paramValue: listingData ?? {
                    ownerFirstName,
                    ownerLastName,
                    companyName,
                    isCurrentOwner,
                    exactSearch,
                }
            },
        ));
    } else if (VOLUME_PARAM in searchFilters
        || FOLIO_PARAM in searchFilters) {
        volumeFolioSearch = true;
        filters.push(builder(
            VOLUME_FOLIO_DATA_PARAM,
            {
                paramValue: volumeFolioData ?? {
                    volumeNumber,
                    folioNumber,
                }
            },
        ));
    } else if (RADIUS_PARAM in searchFilters) {
        filters.push(builder(
            RADIUS_SEARCH_FORMAT,
            {
                radiusParams: {
                    radius: !!radius && parseFloat(radius),
                    lat: !!lat && parseFloat(lat),
                    lon: !!lon && parseFloat(lon),
                }
            },
        ));
        if (!!propertyId) {
            filters.push(builder(
                EXCLUDE_PROPERTY,
                {
                    propertyId: [propertyId],
                },
            ));
        }
    }

    if (!!exists?.length) {
        filters.push({
            operation: AND_OPERATOR,
            filters: exists.map((_exists) => ({
                "field": _exists,
                "operation": EXISTS_OPERATOR,
            }))
        })
    }

    if (statusType === OTM.WITHDRAWN) {
        filters.push(SearchBuilder.buildWithdrawnCompare());
    } else if (statusType === OTM.TENURE) {
        filters.push(builder(
            TENURE_DATA_PARAM,
            { paramValue: { ...tenureData, type: searchFilters.type } },
        ));
    } else if (statusType === OTM.RECENT_SALE) {
        let fromDate = null;
        let toDate = null;
        
        const salesLastSaleContractDate = searchFilters.salesLastSaleContractDate;
        if (salesLastSaleContractDate) {
            if (typeof salesLastSaleContractDate === 'object') {
                if (salesLastSaleContractDate.min) {
                    fromDate = Commons.formatDate(salesLastSaleContractDate.min, 'YYYY-MM-DD', 'YYYYMMDD');
                }
                if (salesLastSaleContractDate.max) {
                    toDate = Commons.formatDate(salesLastSaleContractDate.max, 'YYYY-MM-DD', 'YYYYMMDD');
                }
            } else if (typeof salesLastSaleContractDate === 'string') {
                fromDate = Commons.formatDate(salesLastSaleContractDate, 'YYYY-MM-DD', 'YYYYMMDD');
            }
        }

        const dateRangeFilter = salesLastSaleContractDate
            ? SearchBuilder.dateRangeFilterBuilder({
                param: 'salesLastSaleContractDate',
                paramValue: salesLastSaleContractDate,
            })
            : null;
        const nestedFilter = SearchBuilder.salesListNestedFilterBuilder(fromDate, toDate);

        if (dateRangeFilter && nestedFilter) {
            filters.push({
                operation: OR_OPERATOR,
                filters: [dateRangeFilter, nestedFilter],
            });
        } else if (dateRangeFilter) {
            filters.push(dateRangeFilter);
        } else if (nestedFilter) {
            filters.push(nestedFilter);
        }
    }

    if (propertyIds?.length) {
        buildBatchSearchParamsUsingIds(propertyIds)?.requests[0]?.filters
            ?.forEach(_filter => filters.push(_filter));
    }

    if (ADDRESS_BUILDING_NAME_PARAM in searchFilters) {
        filters.push(builder(ADDRESS_BUILDING_NAME_PARAM, { addressBuildingName, exactSearch }));
    }

    if (TERRITORIES_SEARCH_FORMAT in searchFilters) {
        filters.push(builder(TERRITORIES_SEARCH_FORMAT, { param: TERRITORIES_SEARCH_FORMAT, paramValue: geoPolygon }));
    }

    if (isMapInSearchFilters) {
        if (Object.values(mapSearch).flat().length) {
            const mapSearchFilter = SearchBuilder.mapSearchBuilder({ paramValue: mapSearch }).flat();
            if (mapSearchFilter.length) {
                if (mapSearch.excludeShapes) {
                    filters.push({
                        operation: AND_NOT_OPERATOR,
                        filters: mapSearchFilter,
                    });
                } else {
                    outerFilters.push({
                        operation: OR_OPERATOR,
                        filters: mapSearchFilter,
                    });
                }
            }

            const excludedIdList = mapSearch?.excludedIds || [];
            if (!!excludedIdList?.length) {
                filters.push(builder(
                    EXCLUDE_PROPERTY,
                    {
                        propertyId: excludedIdList,
                    },
                ));
            }
        }
    }

    const flattenFilters = flatten(filters, ownerCompanySearch, volumeFolioSearch);
    if (flattenFilters?.length) {
        outerFilters.push({
            operation: AND_OPERATOR,
            filters: flattenFilters,
        });
    }

    return {
        resultsFormat: buildResultsFormat({ offset, limit, sort, metadata, distinctFields, shape: isMapInSearchFilters && !mapSearch.excludeShapes }, builder),
        filters: outerFilters,
    };
};

const getLocationFilterKeys = () => {
    return Object.values(SUGGEST_FILTERS)
        .concat(RapidSearchConstant.BASE_FILTER_KEYS.radius).concat(RADIUS_PARAM)
        .concat(TERRITORIES_SEARCH_FORMAT)
        .filter(param => param !== 'addressComplete');
}

// As per https://corelogic.atlassian.net/browse/RPSUITE-180636076?focusedCommentId=15939448
// locality search, radius and territory:
//      - drawn shapes won’t be restricted based from locality params (E.g. addressState, councilAreaState, etc).
// non locality search (Name search, Company search, etc):
//      - drawn shapes will be restricted based from non-locality base filters (E.g. `ownerLastName`, `addressState` etc).
const restrictFilterBySearchType = (searchFilters, searchType) => {
    return Object.entries(searchFilters).reduce((acc, [key, value]) => {
        const locationParams = getLocationFilterKeys();
        const locationSearchTypes = [
            ListPropertyPreview.SEARCH_TYPE.ADDRESS,
            ListPropertyPreview.SEARCH_TYPE.RADIUS,
            ListPropertyPreview.SEARCH_TYPE.SAVED_TERRITORY,
        ];
        const isLocationSearchType = locationSearchTypes.some(type => type === searchType);
        if (isLocationSearchType && locationParams.includes(key)) {
            return acc;
        } else {
            return { ...acc, [key]: value };
        }
    }, {});
};

const hasBaseFilters = (searchFilters) => {
    const allBaseParams = getLocationFilterKeys().concat(RapidSearchConstant.NON_LOCALITY_PARAMS);
    return Object.keys(searchFilters).some(key => allBaseParams.includes(key) && !!searchFilters[key]);
};

const buildBatchSearchParams = (searchFilters, sourceOffsets = []) => {
    if (MAP_SEARCH in searchFilters) {
        const { mapSearch, ...others } = searchFilters;
        const isNotEmptyMap = hasBaseFilters(others);
        const requests = [];
        let requestIndex = 0;

        const addedProperties = Object.values(mapSearch?.propertyIds || {})?.flat();
        const removedProperties = mapSearch?.excludedIds || [];

        // combine both excluded and included property ids to avoid duplicates
        const excludedIds = [ ...addedProperties, ...removedProperties ];

        /**
         * Builds a request and, when a per-source offset has been provided for this
         * index, overrides resultsFormat.offset so Show More resumes from the correct
         * page rather than re-fetching pages already present in retainedSearchResults.
         * The index matches the position of this source in the metadata array the API
         * returns, so the caller must push requests in the same order every time.
         */
        const pushRequest = (req) => {
            if (sourceOffsets.length > 0 && sourceOffsets[requestIndex] !== undefined) {
                req.resultsFormat = { ...req.resultsFormat, offset: sourceOffsets[requestIndex] };
            }
            requestIndex++;
            requests.push(req);
        };

        if (isNotEmptyMap) {
            pushRequest(buildRequest({
                ...others,
                // prevents duplicate properties when a map pin or locality is within the drawn shape
                mapSearch: {
                    excludedIds,
                    excludeShapes: true,
                    circle: mapSearch?.circle || [],
                    polygon: mapSearch?.polygon || [],
                    rectangle: mapSearch?.rectangle || [],
                },
            }));
        }

        const restrictedFilters = restrictFilterBySearchType(others, mapSearch?.searchType);
        mapSearch.circle?.forEach(circle => pushRequest(buildRequest({
            ...restrictedFilters,
            mapSearch: {
                circle: [circle], excludedIds,
            },
        })));

        mapSearch.polygon?.forEach(polygon => pushRequest(buildRequest({
            ...restrictedFilters,
            mapSearch: {
                polygon: [polygon], excludedIds,
            },
        })));

        mapSearch.rectangle?.forEach(rectangle => pushRequest(buildRequest({
            ...restrictedFilters,
            mapSearch: {
                rectangle: [rectangle], excludedIds,
            },
        })));

        if (mapSearch.propertyIds
            && Object.values(mapSearch.propertyIds)?.flat()?.length) {
            Object.values(mapSearch.propertyIds).forEach(value => {
                // do not include empty arrays to avoid getting all of properties in the country
                if (value.length) {
                    pushRequest(buildRequest({
                        ...restrictedFilters,
                        mapSearch: {
                            propertyIds: value,
                        },
                    }));
                }
            });
        }

        return {
            requests,
        }
    }

    return {
        requests: [buildRequest(searchFilters)],
    };
};

const buildBatchSearchParamsUsingIds = idList => ({
    requests: [
        {
            resultsFormat: {
                offset: 0,
                limit: 20,
            },
            filters: [
                {
                    operation: 'or',
                    filters: idList.map(id => ({
                        field: 'id',
                        operation: 'equals',
                        value: id,
                    })),
                },
                SearchBuilder.isActiveFilterBuilder({ paramValue: 'true,false' }),
            ],
        },
    ],
});

const buildBatchSearchMultiPageParams = ({ startPage, endPage, filters, propertyIds }) => {
    const requests = [];

    for (let offset = startPage; offset <= endPage; offset += 1) {
        const builtRequests = buildBatchSearchParams({
            ...filters,
            propertyIds,
            offset,
        });
        requests.push(...builtRequests.requests);
    }

    return {
        requests,
    };
};

const getRedirectUrl = (listingParams, redirectPage) => {
    let listingData;
    const {
        isCurrentOwner, state, addressSuburb, ownerName,
        volumeNumber, folioNumber, dispatch, pathName, searchParams,
        holdPeriodHouse, holdPeriodUnit, type, exactSearch,
    } = listingParams;
    const { firstName = '', lastName = '' } = ownerName || {};
    const companyName = firstName && Commons.capitalizeFirstLetter(firstName);
    const isNameSearch = lastName && lastName !== '';
    if (isNameSearch) {
        listingData = {
            isCurrentOwner,
            exactSearch,
            ownerFirstName: firstName && Commons.capitalizeFirstLetter(firstName),
            ownerLastName: lastName && Commons.capitalizeFirstLetter(lastName),
            companyName: '',
            addressState: isNZ ? '' : state && state.toUpperCase(),
            addressSuburb,
        };
    } else {
        listingData = {
            exactSearch,
            isCurrentOwner,
            ownerFirstName: '',
            ownerLastName: '',
            companyName,
            addressState: isNZ ? '' : state && state.toUpperCase(),
            addressSuburb,
        };
    }

    let volumeFolioData;
    const isVolumeFolioSearch = volumeNumber && volumeNumber !== '';
    if (isVolumeFolioSearch) {
        volumeFolioData = {
            volumeNumber,
            folioNumber,
            addressState: isNZ ? '' : state && state.toUpperCase(),
            addressSuburb,
        };
    }

    let tenureData;
    const isTenureSearch = (holdPeriodHouse || holdPeriodUnit)
        && (holdPeriodHouse !== '' || holdPeriodUnit !== '');
    if (isTenureSearch) {
        tenureData = {
            holdPeriodHouse,
            holdPeriodUnit,
            type,
            addressState: isNZ ? '' : state && state.toUpperCase(),
            addressSuburb,
        };
    }

    const _listingData = listingData ? listingData : tenureData;
    const data = volumeFolioData ? volumeFolioData : _listingData;
    const uri = `${pathName}?${searchParams}`;
    passUriToRedirectCallback(data, dispatch, uri, redirectPage);
};

const passUriToRedirectCallback = (data, dispatch, uri, redirectCallback) => {
    // eslint-disable-next-line max-len
    CLApi.getBatchSearch(data).then((result) => {
        const totalElements = Commons.get(result, 'metadata.totalElements');
        const [firstData] = Commons.get(result, 'data');
        const isPdpPath = totalElements === 1;
        let uriToUse;
        if (isPdpPath) {
            uriToUse = routeCodes.PROPERTY.path(firstData.addressComplete, firstData.id);
        } else {
            uriToUse = uri;
        }
        redirectCallback(uriToUse, isPdpPath);
    }).catch(() => {
        dispatch(cruxAppError(ErrorMsg.UNAVAILABLE, ErrorType.PAGE_ERROR));
    }).finally(() => {
        dispatch(cruxUnloader());
    });
}

export default {
    buildSuggestionRapidSearchFilter,
    buildBatchSearchParams,
    buildBatchSearchMultiPageParams,
    getRedirectUrl,
    buildBatchSearchParamsUsingIds,
    passUriToRedirectCallback,
    buildMultiLocalitySuggestion,
    buildRequest,
};
